import { useState, useEffect } from 'react';
import { Lock, KeyRound, Mail, ShieldCheck, LogOut, Loader2, Phone, Search, PhoneCall } from 'lucide-react';
import { startAuthentication } from '@simplewebauthn/browser';
import api from '../lib/api';
import { useAuthStore } from '../stores/auth.store';
import { useBrandingStore } from '../stores/branding.store';
import { isMethodEnabledForUser } from '../lib/authMethods';
import phonebookApi from '../lib/phonebookApi';
import { broadcast, SESSION_MSG } from '../lib/sessionChannel';

// Screen lock overlay.
//
// Replaces what used to happen at the end of the inactivity countdown: a full
// logout and a redirect to /login. That unmounted AppShell, which unmounted
// WebRTCPhone, which called ua.stop() — so the operator's phone went silent
// and incoming calls were lost, for no reason connected to telephony. SIP
// registration never depended on the WFM token in the first place: the PBX
// authenticates the terminal with its own SIP password over WSS.
//
// So the session stays valid and the app stays mounted; this panel just hides
// the data. Two deliberate consequences:
//
//   - z-40, BELOW the softphone's z-50. The phone stays visible and usable on
//     top of the lock: it rings, it resolves the caller against the phonebook,
//     and the call can be answered without unlocking. Answering a call does
//     not require access to the data this panel is hiding.
//   - `locked` is persisted, so reloading the page cannot walk past it.
//
// Unlocking happens IN PLACE. Navigating to /login would unmount the shell and
// take the phone down — the exact failure being fixed. That rules out the
// methods that leave the page (magic link, OIDC redirect).
//
// Which of the rest to OFFER is not a fixed list. v9.76 showed all of them
// unconditionally and produced two dead ends in the first real lock: a
// "Sblocca con passkey" button for someone who has never registered a device,
// and an e-mail code that answered 429 because OTP allows three requests per
// hour per address and the login page had already spent them. On a locked
// screen a dead end is a person locked out of their own session.
//
// So: password first — every account sets one during onboarding, it needs no
// enrolment, no device and no e-mail round trip — then only those methods that
// are enabled for THIS user AND actually enrolled. Enabled and enrolled are
// different questions; passkey and TOTP are asked both.
export default function LockScreen() {
  const user = useAuthStore((s) => s.user);
  const unlock = useAuthStore((s) => s.unlock);
  const logout = useAuthStore((s) => s.logout);

  const branding = useBrandingStore((s) => s.branding);

  const [busy, setBusy] = useState(null);       // 'password' | 'passkey' | 'otp-send' | 'otp' | 'totp' | null
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [phone, setPhone] = useState({ registered: false, active: false });
  // Enrolment, asked of the server — not assumed.
  const [enrolled, setEnrolled] = useState({ passkey: false, totp: false, loaded: false });
  // Read-only phonebook. A locked operator still needs to know who is calling
  // and to look a number up in order to call back; neither requires seeing the
  // data this panel hides. Creating or editing a contact does, so no write
  // affordance is offered here or in the softphone while locked.
  const [pbQuery, setPbQuery] = useState('');
  const [pbResults, setPbResults] = useState([]);
  const [pbBusy, setPbBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [pk, totp] = await Promise.all([
        api.get('/auth/passkey/status').catch(() => null),
        api.get('/auth/totp/status').catch(() => null),
      ]);
      if (cancelled) return;
      setEnrolled({
        passkey: pk?.data?.configured === true,
        totp: totp?.data?.configured === true,
        loaded: true,
      });
    })();
    return () => { cancelled = true; };
  }, []);

  // Mirror the softphone's state so the panel can say, plainly, that calls are
  // still arriving — otherwise a locked screen reads as "everything stopped".
  useEffect(() => {
    const onCall = (e) => setPhone({
      registered: !!e.detail?.registered,
      active: !!e.detail?.active,
    });
    window.addEventListener('wfm-call-state', onCall);
    return () => window.removeEventListener('wfm-call-state', onCall);
  }, []);

  useEffect(() => {
    const q = pbQuery.trim();
    if (q.length < 2) { setPbResults([]); return undefined; }
    let cancelled = false;
    setPbBusy(true);
    const t = setTimeout(async () => {
      try {
        const res = await phonebookApi.search(q);
        if (!cancelled) setPbResults(Array.isArray(res?.data) ? res.data.slice(0, 6) : []);
      } catch (_) {
        if (!cancelled) setPbResults([]);
      } finally {
        if (!cancelled) setPbBusy(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [pbQuery]);

  // The softphone renders ABOVE this panel and already listens for wfm-dial,
  // so dialling from here needs no unlocking. Broadcast too, in case the phone
  // is registered in another window.
  const dial = (number) => {
    try { window.dispatchEvent(new CustomEvent('wfm-dial', { detail: { number } })); } catch (_) {}
    broadcast(SESSION_MSG.dial, { number });
  };

  const email = user?.email || '';
  const allows = (key) => isMethodEnabledForUser(branding, user?.metodi_accesso_disabilitati, key);
  const canPassword = allows('temp_password');
  const canPasskey = allows('passkey') && enrolled.passkey;
  const canOtp = allows('otp');
  const canTotp = allows('totp') && enrolled.totp;
  const nothingAvailable = enrolled.loaded && !canPassword && !canPasskey && !canOtp && !canTotp;

  const fail = (e, fallback) => {
    setError(e?.response?.data?.error?.message || e?.message || fallback);
    setBusy(null);
  };

  // The same endpoint the login page's password flow uses. It authenticates
  // with the password the user chose at onboarding — permanent, not temporary.
  const passwordUnlock = async () => {
    setError(''); setBusy('password');
    try {
      const resp = await api.post('/auth/login/temp', { email, password });
      unlock(resp.data);
      setBusy(null);
    } catch (e) { fail(e, 'Password non valida.'); }
  };

  const passkeyUnlock = async () => {
    setError(''); setBusy('passkey');
    try {
      const challenge = await api.post('/auth/passkey/challenge', { email });
      const assertion = await startAuthentication(challenge.data);
      const verified = await api.post('/auth/passkey/verify', { email, assertion });
      unlock(verified.data);
      setBusy(null);
    } catch (e) {
      fail(e, e?.name === 'NotAllowedError'
        ? 'Autenticazione biometrica annullata.'
        : 'Sblocco con passkey non riuscito.');
    }
  };

  const sendOtp = async () => {
    setError(''); setBusy('otp-send');
    try {
      await api.post('/auth/otp/request', { email });
      setOtpSent(true); setBusy(null);
    } catch (e) { fail(e, 'Invio del codice non riuscito.'); }
  };

  const verifyOtp = async () => {
    setError(''); setBusy('otp');
    try {
      const resp = await api.post('/auth/otp/verify', { email, code: otpCode.trim() });
      unlock(resp.data);
      setBusy(null);
    } catch (e) { fail(e, 'Codice non valido.'); }
  };

  const verifyTotp = async () => {
    setError(''); setBusy('totp');
    try {
      // Same endpoint the login page uses — /auth/totp/verify is the
      // enrolment check, /auth/totp/login is the one that mints a session.
      const resp = await api.post('/auth/totp/login', { email, code: totpCode.trim() });
      unlock(resp.data);
      setBusy(null);
    } catch (e) { fail(e, 'Codice non valido.'); }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--wfm-bg-dark, #0F1B2D)' }}
      role="dialog"
      aria-modal="true"
      aria-label="Schermo bloccato"
    >
      <div className="wfm-card w-full max-w-md p-6 space-y-4" style={{ backgroundColor: 'var(--wfm-bg-card, #FFF)' }}>
        <div className="flex items-center gap-3">
          <div className="wfm-icon-circle"><Lock size={20} /></div>
          <div>
            <h2 className="wfm-h2">Schermo bloccato</h2>
            <p className="wfm-small" style={{ color: 'var(--wfm-text-light)' }}>
              {email || 'Sessione in pausa'}
            </p>
          </div>
        </div>

        <p className="wfm-small">
          I dati sono nascosti per inattività. La sessione è ancora attiva: sblocca per riprendere da dove eri.
        </p>

        {phone.registered && (
          <div className="wfm-toast wfm-toast-success">
            <Phone size={16} />
            <span className="text-sm">
              {phone.active
                ? 'Chiamata in corso — il telefono resta utilizzabile sopra questo pannello.'
                : 'Telefono connesso: continui a ricevere le chiamate anche a schermo bloccato.'}
            </span>
          </div>
        )}

        {error && (
          <div className="wfm-toast wfm-toast-error"><span className="text-sm">{error}</span></div>
        )}

        {/* Read-only phonebook. No create/edit/delete affordance exists here by
            design — this is a lookup, not an editor. */}
        {phone.registered && (
          <div className="space-y-2">
            <label className="wfm-label flex items-center gap-1.5">
              <Search size={14} /> Rubrica (sola lettura)
            </label>
            <input
              className="wfm-input"
              placeholder="Cerca nome o numero…"
              value={pbQuery}
              onChange={(e) => setPbQuery(e.target.value)}
            />
            {pbBusy && <p className="wfm-tiny" style={{ color: 'var(--wfm-text-light)' }}>Ricerca…</p>}
            {!pbBusy && pbQuery.trim().length >= 2 && pbResults.length === 0 && (
              <p className="wfm-tiny" style={{ color: 'var(--wfm-text-light)' }}>Nessun contatto trovato.</p>
            )}
            {pbResults.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 p-2 rounded-lg border" style={{ borderColor: 'var(--wfm-border)' }}>
                <div className="min-w-0">
                  <p className="wfm-small font-semibold truncate">{c.name}</p>
                  <p className="wfm-tiny truncate" style={{ color: 'var(--wfm-text-light)' }}>
                    {(c.numbers || []).map((n) => n.numeroE164).filter(Boolean).join(' · ') || '—'}
                  </p>
                </div>
                {(c.numbers || []).some((n) => n.numeroE164) && (
                  <button
                    type="button"
                    className="wfm-btn wfm-btn-secondary wfm-btn-sm"
                    title="Chiama"
                    onClick={() => dial(c.numbers[0].numeroE164)}
                  >
                    <PhoneCall size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {nothingAvailable && (
          <div className="wfm-toast wfm-toast-warning">
            <span className="text-sm">
              Nessun metodo di sblocco disponibile per il tuo account. Usa "Esci e chiudi la
              sessione" e rientra dalla pagina di accesso.
            </span>
          </div>
        )}

        <div className="space-y-2">
          {canPassword && (
            <div className="flex gap-2">
              <input
                className="wfm-input flex-1"
                type="password"
                autoComplete="current-password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && password) passwordUnlock(); }}
              />
              <button type="button" className="wfm-btn wfm-btn-primary" onClick={passwordUnlock} disabled={!!busy || !password}>
                {busy === 'password' ? <Loader2 size={16} className="animate-spin" /> : 'Sblocca'}
              </button>
            </div>
          )}

          {canPasskey && (
            <button type="button" className="wfm-btn wfm-btn-secondary w-full" onClick={passkeyUnlock} disabled={!!busy}>
              {busy === 'passkey' ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
              Sblocca con passkey
            </button>
          )}

          {canOtp && (!otpSent ? (
            <button type="button" className="wfm-btn wfm-btn-secondary w-full" onClick={sendOtp} disabled={!!busy}>
              {busy === 'otp-send' ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
              Inviami un codice via email
            </button>
          ) : (
            <div className="flex gap-2">
              <input
                className="wfm-input flex-1"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Codice ricevuto via email"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && otpCode.trim()) verifyOtp(); }}
              />
              <button type="button" className="wfm-btn wfm-btn-primary" onClick={verifyOtp} disabled={!!busy || !otpCode.trim()}>
                {busy === 'otp' ? <Loader2 size={16} className="animate-spin" /> : 'Sblocca'}
              </button>
            </div>
          ))}

          {canTotp && (
            <div className="flex gap-2">
              <input
                className="wfm-input flex-1"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Codice app autenticazione (TOTP)"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && totpCode.trim()) verifyTotp(); }}
              />
              <button type="button" className="wfm-btn wfm-btn-secondary" onClick={verifyTotp} disabled={!!busy || !totpCode.trim()}>
                {busy === 'totp' ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          className="wfm-btn wfm-btn-ghost w-full"
          onClick={() => logout().then(() => { window.location.href = '/login'; })}
          disabled={!!busy}
          style={{ color: 'var(--wfm-error)' }}
        >
          <LogOut size={16} /> Esci e chiudi la sessione
        </button>
      </div>
    </div>
  );
}
